import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { buildErrorMessage } from '@constants'
import { getSupportsOfElement } from '@domainUtils'
import { AxiosError } from 'axios'
import { every, filter, find, some, flatten, uniq, reject, omit, isEqual } from 'lodash-es'
import { Stack } from '@mui/material'
import { ElementCSTable } from '@editor/components'
import {
  useElementCrossSectionAssignment,
  useGuidToCrossSection,
  useInnerForcesEnvelopeInvalidation,
} from '@editor/hooks'
import { useResultsStore, useTransmissionStore } from '@editor/stores'
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { Form2 } from '@ui/forms'
import { enqueueSnackbar } from '@ui/snackbar'
import { getModel, postCalcPositionChecks, SupportingElementCSConfig } from '@queries'
import {
  saveCrossSectionsOnElement,
  saveSingleMemberCheckSettings,
  updateElementCrossSectionPolicy,
} from '@mutations'
import FormFields from './components/FormFields'
import MultiEditSubmitButton from './components/MultiEditSubmitButton'
import SyncCrossSectionWithScene from './components/SyncCrossSectionWithScene'

export interface DefaultValues {
  crossSection?: CrossSection
  crossSectionPolicy?: CrossSectionPolicy
  elements: {
    guid: string
    crossSection: CrossSection
    crossSectionPolicy?: CrossSectionPolicy
    checks: CombinedPositionCheck[]
    checkSettings: SettingsOnMember
    supportingElementCSConfig: SupportingElementCSConfig[]
  }[]
}

interface Props {
  elementGuids: string[]
  referenceElementGuid: string
  referenceElementType: ElementTypes
}

const MultiEditCrossSection = ({
  elementGuids,
  referenceElementGuid,
  referenceElementType,
}: Props) => {
  const queryClient = useQueryClient()
  const { projectId } = useParams()
  const guidToCrossSection = useGuidToCrossSection()
  const elementCrossSectionAssignment = useElementCrossSectionAssignment()
  const invalidateInnerForcesEnvelopes = useInnerForcesEnvelopeInvalidation()
  const setSingleMemberCheckSetting = useResultsStore(state => state.setSingleMemberCheckSetting)

  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const setStructuralChecks = useResultsStore(state => state.setStructuralChecks)

  const structuralChecksRef = useLatest(structuralChecks)
  const isPolicyElementType = referenceElementType === 'rips' || referenceElementType === 'lintels'

  const equalCs = useMemo(() => {
    if (elementGuids.length === 0) return false

    const referenceCS = omit(guidToCrossSection[elementGuids[0]], 'element_guid')

    return every(elementGuids, guid => {
      const csWithoutElement = omit(guidToCrossSection[guid], 'element_guid')
      return isEqual(csWithoutElement, referenceCS)
    })
  }, [guidToCrossSection, elementGuids])

  const equalPolicy = useMemo(() => {
    if (!isPolicyElementType || elementGuids.length === 0) return undefined

    const referencePolicy =
      find(elementCrossSectionAssignment, { element_guid: elementGuids[0] })?.crossSectionPolicy ??
      'UserDefined'

    const allEqual = every(elementGuids, guid => {
      const policy =
        find(elementCrossSectionAssignment, { element_guid: guid })?.crossSectionPolicy ??
        'UserDefined'
      return policy === referencePolicy
    })

    return allEqual ? referencePolicy : undefined
  }, [elementCrossSectionAssignment, elementGuids, isPolicyElementType])

  const { mutateAsync: recomputeChecks } = useMutation({
    mutationKey: [projectId, 'recomputeChecksMultiEdit'],
    mutationFn: async (elementGuids: string[]) => {
      return postCalcPositionChecks.request(projectId, elementGuids)
    },
    // the recomputed member results of the new cross section change the envelopes
    onSuccess: () => invalidateInnerForcesEnvelopes(projectId as string),
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Neuberechnen der Nachweise'), {
        variant: 'error',
      })
    },
  })

  const { mutateAsync, isLoading } = useMutation(
    async (data: DefaultValues) => {
      if (isPolicyElementType && data.crossSectionPolicy === 'AssemblyStandard') {
        await updateElementCrossSectionPolicy.request(projectId, {
          element_guids: elementGuids,
          crossSectionPolicy: 'AssemblyStandard',
          userDefinedCrossSection: null,
        })

        return {
          savedCrossSections: [] as CrossSectionOnElement[],
          savedSettings: [] as SettingsOnMember[],
          newChecks: [] as CombinedPositionCheck[],
        }
      }

      const crossSectionsToAssign: CrossSectionOnElement[] = data.elements.map(element => ({
        ...(data.crossSection as CrossSection),
        element_guid: element.guid,
      }))

      if (crossSectionsToAssign.length > 0) {
        await saveCrossSectionsOnElement.request(projectId, crossSectionsToAssign)
      }

      const settingsToSave: SettingsOnMember[] = data.elements.map(element => element.checkSettings)

      const savedSettings: SettingsOnMember[] = []

      const resultsAvailable = some(data.elements, element => element.checks.length > 0)
      if (resultsAvailable) {
        for (const setting of settingsToSave) {
          const savedSetting = await saveSingleMemberCheckSettings.request(projectId, setting)
          savedSettings.push(savedSetting)
        }
      }

      const newChecks = flatten(data.elements.map(element => element.checks))

      return {
        savedCrossSections: crossSectionsToAssign,
        savedSettings,
        newChecks,
      }
    },
    {
      onSuccess: async ({ savedCrossSections, savedSettings, newChecks }) => {
        for (const setting of savedSettings) {
          setSingleMemberCheckSetting(setting)
        }

        const elementGuids = uniq(newChecks.map(check => check.element_guid))

        const replacedStructuralChecks = [
          ...reject(structuralChecksRef.current, check =>
            elementGuids.includes(check.element_guid),
          ),
          ...newChecks,
        ]
        setStructuralChecks(replacedStructuralChecks)

        if (savedSettings.length > 0) {
          const beamLike = savedCrossSections.map(crossSection => crossSection.element_guid)
          recomputeChecks(beamLike)
        }

        // Always refetch the planar model. StoreReInitializer overwrites local
        // elementCrossSectionAssignment from derived model props on every model
        // refresh, so optimistic setSingleCrossSection alone does not stick.
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        enqueueSnackbar('Querschnitt erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Querschnitts'), {
          variant: 'error',
        })
        // Refetch onError to ensure local state is in sync with backend again
        queryClient.invalidateQueries(getModel.getKey(projectId))
      },
    },
  )

  const verticalGraph = useTransmissionStore(state => state.verticalTransmissionGraph)

  const defaultValues = useMemo(() => {
    return {
      // Gate editing behind "Ändern" when selected elements have different CS so
      // the scene/table keep each element's real CS until the user opts in.
      crossSection: equalCs ? guidToCrossSection[referenceElementGuid] : undefined,
      crossSectionPolicy: equalPolicy,
      elements: elementGuids.map(guid => {
        const checks = filter(structuralChecks, check => check.element_guid === guid)
        const checkSettings = find(memberCheckSettings, settings => settings.element_guid === guid)
        const supportingElementCSConfig: SupportingElementCSConfig[] = verticalGraph
          ? getSupportsOfElement(guid, verticalGraph)
              .map(({ guid: supportingGuid }) => {
                const assignment = find(elementCrossSectionAssignment, {
                  element_guid: supportingGuid,
                })
                return assignment
                  ? { element_guid: assignment.element_guid, cross_section: assignment.element_cs }
                  : null
              })
              .filter((c): c is SupportingElementCSConfig => c != null)
          : []

        return {
          guid,
          crossSection: guidToCrossSection[guid],
          crossSectionPolicy: isPolicyElementType
            ? find(elementCrossSectionAssignment, { element_guid: guid })?.crossSectionPolicy ??
              'UserDefined'
            : undefined,
          checks,
          checkSettings,
          supportingElementCSConfig,
        }
      }),
    }
  }, [
    equalCs,
    equalPolicy,
    guidToCrossSection,
    referenceElementGuid,
    elementGuids,
    elementCrossSectionAssignment,
    isPolicyElementType,
    structuralChecks,
    memberCheckSettings,
    verticalGraph,
  ])

  const isUpdatingChecks = useIsMutating([projectId, 'updateChecksMultiEdit']) > 0

  return (
    <Stack direction="column" spacing={2} width="100%" pt={1}>
      <Form2
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSubmit={data => mutateAsync(data as DefaultValues)}
        data-cy="crosssection-form"
      >
        <Stack direction="column" spacing={1}>
          <FormFields
            disabled={referenceElementType === 'standard_rips'}
            elementType={referenceElementType}
            showPolicyToggle={isPolicyElementType}
          />

          <ElementCSTable />

          <MultiEditSubmitButton loading={isLoading} isUpdatingChecks={isUpdatingChecks} />
        </Stack>

        <SyncCrossSectionWithScene />
      </Form2>
    </Stack>
  )
}

export default MultiEditCrossSection
